import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { AlertTriangle, Copy, Loader2, Mail, RefreshCw, Save, Send } from "lucide-react";
import { toast } from "sonner";
import { TopBar } from "@/components/layout/TopBar";
import { SendDialog } from "@/components/weekly-update/SendDialog";
import { useSaveEditorial, useSendWeeklyUpdate, useWeeklyUpdate } from "@/hooks/useWeeklyUpdate";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCan } from "@/lib/roles";
import { cn } from "@/lib/cn";

/**
 * Weekly Update — the Monday Top-10 board email, previewed exactly as
 * it will send (Brett, 2026-09-21). Built verbatim from planner data:
 * health, latest Status Pulse, red/yellow milestones with their
 * go-to-green plan, RAID count, benefit, and "changed since last week"
 * from the Monday snapshot. Nothing is edited here — fix the source on
 * the project page and refresh. The worker emails the draft to
 * WEEKLY_UPDATE_TO every Monday at noon; the buttons below do the same
 * on demand. "Send to…" (2026-09-22) is the reviewed publish step:
 * planner users and/or typed addresses, remembered on the profile.
 *
 * Jim K's second review (2026-10-26): the PMO writes the intro and a
 * standing "You should know" here (not in Outlook); a "Before you send"
 * panel lists the pre-send checks, and Send to… refuses until they're
 * clean or waived with "Send anyway".
 */
export function WeeklyUpdate() {
  const q = useWeeklyUpdate();
  const send = useSendWeeklyUpdate();
  const { user, updateUser } = useAuth();
  const { isPmo } = useCan();
  const [copied, setCopied] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [waive, setWaive] = useState(false);
  const saveEditorial = useSaveEditorial();
  const [intro, setIntro] = useState("");
  const [ysk, setYsk] = useState("");
  const editorial = q.data?.data.editorial;
  useEffect(() => {
    if (editorial) { setIntro(editorial.intro ?? ""); setYsk(editorial.you_should_know ?? ""); }
  }, [editorial]);
  const editorialDirty = !!editorial && (intro !== (editorial.intro ?? "") || ysk !== (editorial.you_should_know ?? ""));
  const checks = q.data?.data.checks ?? [];
  const remembered = useMemo(() => user?.weekly_update_recipients ?? [], [user?.weekly_update_recipients]);

  const copy = async () => {
    if (!q.data) return;
    try {
      const item = new ClipboardItem({
        "text/html": new Blob([q.data.html], { type: "text/html" }),
        "text/plain": new Blob([q.data.text], { type: "text/plain" }),
      });
      await navigator.clipboard.write([item]);
    } catch {
      await navigator.clipboard.writeText(q.data.text);
    }
    setCopied(true);
    toast.success("Copied — paste into a new Outlook message");
    setTimeout(() => setCopied(false), 2000);
  };

  const emailMe = () => {
    send.mutate(
      {},
      {
        onSuccess: (r) =>
          r.sent
            ? toast.success(`Draft emailed to ${r.to.join(", ")} — snapshot taken for next week's diff`)
            : toast.error("Email isn't configured on this environment; snapshot taken anyway"),
        onError: (e) => toast.error((e as Error).message || "Couldn't send"),
      },
    );
  };

  const sendTo = (to: string[]) => {
    send.mutate(
      { to, waive_checks: waive },
      {
        onSuccess: async (r) => {
          setSendOpen(false);
          if (r.sent) toast.success(`Sent to ${r.to.join(", ")} — snapshot taken for next week's diff`);
          else toast.error("Email isn't configured on this environment; snapshot taken anyway");
          // Remember the list for next week (self-service profile field).
          try {
            const { data } = await api.patch("/auth/me", { weekly_update_recipients: r.to });
            if (user) updateUser({ ...user, weekly_update_recipients: data.weekly_update_recipients ?? r.to });
          } catch {
            /* the send already happened; a failed remember is not worth a toast */
          }
        },
        onError: (e) => {
          const status = (e as { response?: { status?: number } }).response?.status;
          if (status === 409) {
            setSendOpen(false);
            toast.error("Pre-send checks are outstanding — fix them or tick Send anyway");
            return;
          }
          toast.error((e as Error).message || "Couldn't send");
        },
      },
    );
  };

  const h = q.data?.data.headline;

  return (
    <>
      <TopBar
        title="Weekly Update"
        subtitle="The Monday Top 10 email, built from the planner — health, pulse, go-to-green, and what changed since last week."
      >
        <button
          onClick={() => q.refetch()}
          disabled={q.isFetching}
          className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-medium text-ink-3 transition-colors hover:bg-raised disabled:opacity-50"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", q.isFetching && "animate-spin")} /> Refresh
        </button>
        <button
          onClick={copy}
          disabled={!q.data}
          className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-xs font-medium text-ink-3 transition-colors hover:bg-raised disabled:opacity-50"
          title="Copy the email (formatted) to paste into Outlook"
        >
          <Copy className="h-3.5 w-3.5" /> {copied ? "Copied" : "Copy email"}
        </button>
        <button
          onClick={emailMe}
          disabled={!q.data || send.isPending}
          className="flex items-center gap-1.5 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-brand-navy-700 disabled:opacity-50"
          title={`Send this draft to ${user?.email ?? "you"} and snapshot the Top 10`}
          data-testid="email-me"
        >
          {send.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
          Email me the draft
        </button>
        {isPmo && (
        <button
          onClick={() => setSendOpen(true)}
          disabled={!q.data || send.isPending}
          className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
          title="Send this update to a list of people"
          data-testid="send-to"
        >
          <Send className="h-3.5 w-3.5" />
          Send to…
        </button>
        )}
      </TopBar>

      {q.data && (
        <SendDialog
          open={sendOpen}
          onOpenChange={setSendOpen}
          initial={remembered}
          subject={q.data.subject}
          sending={send.isPending}
          onSend={sendTo}
        />
      )}

      <div className="space-y-4 p-8" data-testid="weekly-update">
        {q.isLoading && (
          <div className="rounded-xl border border-line bg-surface p-12 text-center text-sm text-ink-muted">
            Building the update…
          </div>
        )}
        {q.isError && (
          <div className="rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-6 text-sm text-red-800 dark:text-red-300">
            Couldn't build the update: {(q.error as Error).message}
          </div>
        )}
        {q.data && h && (
          <>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-muted">
              <span>
                Subject: <span className="font-medium text-ink">{q.data.subject}</span>
              </span>
              <span>
                {q.data.data.since
                  ? `Changes measured since the ${q.data.data.since} snapshot`
                  : "No prior snapshot yet — the first send starts the week-over-week diff"}
              </span>
              {h.stale > 0 && (
                <span className="text-amber-700 dark:text-amber-300">
                  {h.stale} Top 10 row{h.stale === 1 ? "" : "s"} with no pulse this week — fix on the{" "}
                  <Link to="/top-projects" className="underline">board</Link> before sending
                </span>
              )}
            </div>
            {isPmo && (
              <div className="rounded-xl border border-line bg-surface p-4" data-testid="weekly-editorial">
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Your words — written here, not in Outlook</div>
                  <button
                    onClick={() => saveEditorial.mutate({ intro, you_should_know: ysk }, {
                      onSuccess: () => toast.success("Saved — the preview below is updated"),
                      onError: (e) => toast.error((e as Error).message || "Couldn't save"),
                    })}
                    disabled={!editorialDirty || saveEditorial.isPending}
                    className="flex items-center gap-1.5 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-navy-700 disabled:opacity-50"
                    data-testid="editorial-save"
                  >
                    <Save className="h-3.5 w-3.5" /> Save
                  </button>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="block text-xs">
                    <span className="mb-1 block font-semibold text-ink-muted">Intro paragraph</span>
                    <textarea value={intro} onChange={(e) => setIntro(e.target.value)} rows={4} placeholder="One or two sentences that open the email." className="w-full rounded-lg border border-line bg-canvas p-2 text-sm" data-testid="editorial-intro" />
                  </label>
                  <label className="block text-xs">
                    <span className="mb-1 block font-semibold text-ink-muted">You should know (standing section)</span>
                    <textarea value={ysk} onChange={(e) => setYsk(e.target.value)} rows={4} placeholder="Cost avoidance, decisions coming, anything the board should hear from you first." className="w-full rounded-lg border border-line bg-canvas p-2 text-sm" data-testid="editorial-ysk" />
                  </label>
                </div>
                <div className="mt-1 text-[11px] text-ink-muted">Markdown works (**bold**, bullets). Leave a box empty to drop that section.</div>
              </div>
            )}
            {checks.length > 0 && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm dark:border-amber-500/40 dark:bg-amber-500/10" data-testid="weekly-checks">
                <div className="mb-1 flex items-center gap-2 font-semibold text-amber-900 dark:text-amber-200">
                  <AlertTriangle className="h-4 w-4" /> Before you send — {checks.length} thing{checks.length === 1 ? "" : "s"} the board would ask about
                </div>
                <ul className="list-disc space-y-0.5 pl-6 text-amber-900 dark:text-amber-100">
                  {checks.map((c, i) => (
                    <li key={i}>
                      <Link to={`/portfolio/${c.project_id}`} className="font-mono text-xs underline">{c.project_id}</Link>{" "}
                      {c.text.replace(new RegExp(`^${c.project_id}\\s*`), "")}
                    </li>
                  ))}
                </ul>
                <div className="mt-2 text-xs text-amber-900/80 dark:text-amber-200/80">These never print in the email. Fix them on the project page, or tick to send anyway.</div>
                {isPmo && (
                  <label className="mt-2 flex items-center gap-2 text-xs font-medium text-amber-900 dark:text-amber-200">
                    <input type="checkbox" checked={waive} onChange={(e) => setWaive(e.target.checked)} className="h-4 w-4 rounded border-line" data-testid="waive-checks" />
                    Send anyway
                  </label>
                )}
              </div>
            )}
            {/* The email, exactly as it renders. Pulse text is Markdown
                people typed, so sanitize before injecting. */}
            <div
              className="rounded-xl border border-line bg-surface p-6"
              data-testid="weekly-preview"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(q.data.html) }}
            />
          </>
        )}
      </div>
    </>
  );
}
